/**
 * Prototype mode's picture (Figma's), the overlay of UI/UX's board mode: from every element
 * whose click leads somewhere (`style.navigate`), a blue curve to the screen it leads to
 * (`[data-interaction=<item id>]`), or a short "Back" one; and on the one item selected, the round
 * handle (`[data-proto-handle=<id>]`, `data-mode-drag`) dragged onto a screen to make a click lead
 * there (onto nothing: to nowhere). Drawn in board units inside the board's layer; the board's
 * gestures carry the drag and hand it back here (`dragging`), then to the mode's `drop`.
 */
import { For, Show, type JSX } from "solid-js";
import { useApp, usePack } from "../../app/context";
import type { Point } from "../../state/board";
import type { ModeDrag } from "../../state/packs";
import type { UiUx } from "./service";

/** A curve from `a` (leaving to the right) to `b` (arriving from the left), as an SVG path. */
function curve(a: Point, b: Point): string {
  const bend = Math.max(60, Math.abs(b.x - a.x) / 2);
  return `M${a.x},${a.y} C${a.x + bend},${a.y} ${b.x - bend},${b.y} ${b.x},${b.y}`;
}

/** The overlay. `dragging`: the drag from a handle in progress. */
export function Interactions(props: { dragging: ModeDrag | null }): JSX.Element {
  const { board, graph } = useApp();
  const uiux = usePack<UiUx>("uiux");
  const k = () => board.camera().k;
  /** The ways out drawn: from the element's right side to the target screen's left side. */
  const ways = () => {
    const content = board.content();
    const screens = new Map(uiux.screens().map((s) => [s.id, s.place]));
    return content.items.flatMap((item) => {
      const to = item.node.style?.navigate;
      if (!to) return [];
      const p = item.place;
      const from = { x: p.x + p.w, y: p.y + p.h / 2 };
      if (to === "back") return [{ id: item.id, from, to: { x: from.x + 70, y: from.y - 36 }, back: true }];
      const target = screens.get(to);
      if (!target) return [];
      return [{ id: item.id, from, to: { x: target.x, y: target.y + Math.min(60, target.h / 2) }, back: false }];
    });
  };
  const chosen = () => {
    const ids = board.selected();
    if (ids.length !== 1 || !graph.state.graph.nodes.some((n) => n.id === ids[0])) return null;
    const p = board.content().places.get(ids[0]!);
    // Beside the right side, clear of the resize handle there.
    return p ? { id: ids[0]!, x: p.x + p.w + 16 / k(), y: p.y + p.h / 2 } : null;
  };
  return (
    <>
      <svg class="board-arrows board-ways" aria-hidden="true">
        <For each={ways()}>
          {(w) => (
            <g class="board-way" data-interaction={w.id}>
              <path d={curve(w.from, w.to)} fill="none" stroke-width={2 / Math.min(1, k())} />
              <circle cx={w.from.x} cy={w.from.y} r={4 / Math.min(1, k())} />
              <polygon points={`${w.to.x},${w.to.y} ${w.to.x - 10},${w.to.y - 5} ${w.to.x - 10},${w.to.y + 5}`} />
              <Show when={w.back}>
                <text x={w.to.x + 4} y={w.to.y + 4} class="board-way-label">
                  Back
                </text>
              </Show>
            </g>
          )}
        </For>
        <Show when={props.dragging}>
          {(l) => {
            const from = () => {
              const p = board.content().places.get(l().from);
              return p ? { x: p.x + p.w, y: p.y + p.h / 2 } : null;
            };
            return <Show when={from()}>{(f) => <path class="board-way-drawn" d={curve(f(), l().to)} fill="none" />}</Show>;
          }}
        </Show>
      </svg>
      <Show when={chosen()}>
        {(c) => <div class="board-proto-handle" data-proto-handle={c().id} data-mode-drag={c().id} title="Drag onto a screen: a click here leads there" style={{ left: `${c().x}px`, top: `${c().y}px`, transform: `translate(-50%, -50%) scale(${1 / k()})` }} />}
      </Show>
    </>
  );
}
